import { Fighter, getFighterRange, canFighterAttack, getDefenseCost, getAttackCost } from '../types/fighter';
import { GameCell, getMoveDistance, canMoveTo, canAttackInLine, calculatePath } from '../types/game';

export type AiAction =
  | { type: 'move'; x: number; y: number; distance: number }
  | { type: 'attack'; targetId: string }
  | { type: 'defend' }
  | { type: 'end' };

export function aiTurn(aiFighter: Fighter, allFighters: Fighter[], map: GameCell[][]): AiAction {
  // Находим ближайшего врага
  const enemies = allFighters.filter(f => f.isPlayer && f.health > 0);
  if (enemies.length === 0) {
    return { type: 'end' };
  }

  // Находим ближайшего врага
  let nearestEnemy = enemies[0];
  let minDistance = getMoveDistance(aiFighter.x, aiFighter.y, nearestEnemy.x, nearestEnemy.y);

  for (const enemy of enemies) {
    const dist = getMoveDistance(aiFighter.x, aiFighter.y, enemy.x, enemy.y);
    if (dist < minDistance) {
      minDistance = dist;
      nearestEnemy = enemy;
    }
  }

  const attackRange = getFighterRange(aiFighter);
  const attackCost = getAttackCost(aiFighter);
  const defenseCost = getDefenseCost(aiFighter);
  const moveDistanceToEnemy = getMoveDistance(aiFighter.x, aiFighter.y, nearestEnemy.x, nearestEnemy.y);

  // Проверяем, мало ли жизней (менее 30% от максимума)
  const healthPercent = aiFighter.health / aiFighter.maxHealth;
  const isLowHealth = healthPercent < 0.3;

  // Если можем атаковать прямо сейчас - атакуем
  if (canAttackInLine(aiFighter.x, aiFighter.y, nearestEnemy.x, nearestEnemy.y, attackRange, map) && canFighterAttack(aiFighter)) {
    return { type: 'attack', targetId: nearestEnemy.id };
  }

  // Если не можем атаковать, ищем кратчайший путь к врагу
  if (!canAttackInLine(aiFighter.x, aiFighter.y, nearestEnemy.x, nearestEnemy.y, attackRange, map) && aiFighter.stamina > 0) {
    // Ищем позицию для атаки: ближайшую к врагу, с которой можно атаковать
    let bestAttackPosition: { x: number; y: number; pathLength: number; pathCost: number } | null = null;
    
    // Проверяем все клетки вокруг врага в пределах attackRange
    for (let dx = -attackRange; dx <= attackRange; dx++) {
      for (let dy = -attackRange; dy <= attackRange; dy++) {
        const targetX = nearestEnemy.x + dx;
        const targetY = nearestEnemy.y + dy;
        
        // Проверяем, что клетка доступна и с неё можно атаковать
        if (canMoveTo(map, targetX, targetY) && 
            canAttackInLine(targetX, targetY, nearestEnemy.x, nearestEnemy.y, attackRange, map)) {
          
          // Находим путь к этой позиции
          const path = calculatePath(aiFighter.x, aiFighter.y, targetX, targetY, map);
          if (path.length === 0) continue;
          
          // Вычисляем стоимость пути
          let pathCost = 0;
          let currentX = aiFighter.x;
          let currentY = aiFighter.y;
          for (const step of path) {
            const stepCost = getMoveDistance(currentX, currentY, step.x, step.y);
            pathCost += stepCost;
            currentX = step.x;
            currentY = step.y;
          }
          
          // Проверяем, хватает ли стамины на движение + атаку
          if (pathCost + attackCost <= aiFighter.stamina) {
            // Выбираем кратчайший путь
            if (!bestAttackPosition || pathCost < bestAttackPosition.pathCost) {
              bestAttackPosition = { x: targetX, y: targetY, pathLength: path.length, pathCost };
            }
          }
        }
      }
    }
    
    // Если нашли позицию для атаки - идём к ней (до конца пути)
    if (bestAttackPosition) {
      const path = calculatePath(aiFighter.x, aiFighter.y, bestAttackPosition.x, bestAttackPosition.y, map);
      if (path.length > 0) {
        // Двигаемся до конечной позиции пути, если хватает стамины
        const finalPosition = path[path.length - 1];
        if (bestAttackPosition.pathCost <= aiFighter.stamina) {
          return { type: 'move', x: finalPosition.x, y: finalPosition.y, distance: bestAttackPosition.pathCost };
          }
        }
      }
    
    // Если не можем дойти до позиции атаки, просто приближаемся к врагу кратчайшим путём
    // Ищем ближайшую доступную клетку к врагу
    let bestMovePosition: { x: number; y: number; pathCost: number; distance: number } | null = null;
    const maxMoveDistance = aiFighter.stamina;
    
    // Проверяем клетки вокруг врага
    for (let dx = -maxMoveDistance; dx <= maxMoveDistance; dx++) {
      for (let dy = -maxMoveDistance; dy <= maxMoveDistance; dy++) {
        const targetX = nearestEnemy.x + dx;
        const targetY = nearestEnemy.y + dy;
        
        if (canMoveTo(map, targetX, targetY)) {
          const path = calculatePath(aiFighter.x, aiFighter.y, targetX, targetY, map);
          if (path.length === 0) continue;
          
          // Вычисляем стоимость пути
          let pathCost = 0;
          let currentX = aiFighter.x;
          let currentY = aiFighter.y;
          for (const step of path) {
            const stepCost = getMoveDistance(currentX, currentY, step.x, step.y);
            pathCost += stepCost;
            currentX = step.x;
            currentY = step.y;
          }
          
          if (pathCost <= maxMoveDistance) {
            const distance = getMoveDistance(targetX, targetY, nearestEnemy.x, nearestEnemy.y);
            // Выбираем ближайшую к врагу позицию с кратчайшим путём
            if (!bestMovePosition || distance < bestMovePosition.distance || 
                (distance === bestMovePosition.distance && pathCost < bestMovePosition.pathCost)) {
              bestMovePosition = { x: targetX, y: targetY, pathCost, distance };
            }
            }
          }
        }
      }

    // Если нашли позицию для движения - идём к ней (до конца пути)
    if (bestMovePosition) {
      const path = calculatePath(aiFighter.x, aiFighter.y, bestMovePosition.x, bestMovePosition.y, map);
      if (path.length > 0) {
        // Двигаемся до конечной позиции пути, если хватает стамины
        const finalPosition = path[path.length - 1];
        if (bestMovePosition.pathCost <= aiFighter.stamina) {
          return { type: 'move', x: finalPosition.x, y: finalPosition.y, distance: bestMovePosition.pathCost };
        }
      }
    }
  }

  // Если не хватает стамины на атаку или мало жизней - используем щит
  if (defenseCost > 0 && !aiFighter.isDefending && aiFighter.stamina >= defenseCost) {
    // Используем щит если:
    // 1. Не хватает стамины на атаку после движения
    // 2. Мало жизней (менее 30%)
    // 3. Враг близко (в пределах атаки + 1 клетка)
    const canAttackAfterMove = aiFighter.stamina >= attackCost + 1; // Хотя бы 1 стамина на движение
    if (isLowHealth || !canAttackAfterMove || moveDistanceToEnemy <= attackRange + 1) {
      return { type: 'defend' };
    }
  }

  return { type: 'end' };
}

